import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Shield, Download, Upload, Mail, FileText, Clock, AlertTriangle, Calendar } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format, addDays, addMonths, differenceInDays, differenceInCalendarDays } from "date-fns";
import { parseDateLocal } from "@/lib/utils";

export default function COBRADetails() {
  const [location, setLocation] = useLocation();
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeContext, setComposeContext] = useState<{participantId:number; type:string}|null>(null);
  const [messageDraft, setMessageDraft] = useState("Your notice has been prepared.");
  // Use authenticated user context for client scoping
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });
  const activeClientId = user?.clientId as number | undefined;
  // Fetch employees explicitly (scoped by client)
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", activeClientId, "employees"],
    enabled: !!activeClientId,
    queryFn: async () => {
      if (!activeClientId) return [];
      const res = await fetch(`/api/clients/${activeClientId}/employees`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    }
  });
  const qc = useQueryClient();
  // Persisted COBRA participants
  const { data: persistedParticipants = [] } = useQuery<any[]>({
    queryKey: ["/api/cobra/participants", activeClientId],
    enabled: !!activeClientId,
    queryFn: async () => {
      const res = await fetch(`/api/cobra/participants?clientId=${activeClientId}`);
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Notices and payments (optional for rollups)
  const { data: cobraNotices = [] } = useQuery<any[]>({
    queryKey: ["/api/cobra/notices", activeClientId],
    enabled: !!activeClientId,
    queryFn: async () => {
      const res = await fetch(`/api/cobra/notices?clientId=${activeClientId}`);
      if (!res.ok) return [];
      return res.json();
    }
  });
  const { data: cobraPayments = [] } = useQuery<any[]>({
    queryKey: ["/api/cobra/payments", activeClientId],
    enabled: !!activeClientId,
    queryFn: async () => {
      const res = await fetch(`/api/cobra/payments?clientId=${activeClientId}`);
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Load enrollments for premium computation (server computes premiums when missing)
  const { data: allEnrollments = [] } = useQuery<any[]>({
    queryKey: ["/api/enrollments", activeClientId],
    enabled: !!activeClientId,
    queryFn: async () => {
      const res = await fetch(`/api/enrollments?clientId=${activeClientId}`);
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Helper: find all active enrollments as of a given date; fallback to most recent set if none active
  const findEnrollmentsAsOf = (employeeId: number, asOf: Date) => {
    const list = allEnrollments.filter((e: any) => Number(e.employeeId) === Number(employeeId));
    if (list.length === 0) return [] as any[];
    const asOfTs = asOf.getTime();
    const active = list.filter((e: any) => {
      const eff = e.effectiveDate ? (parseDateLocal(e.effectiveDate)?.getTime() ?? 0) : 0;
      const end = e.endDate ? (parseDateLocal(e.endDate)?.getTime() ?? Infinity) : Infinity;
      return eff <= asOfTs && end >= asOfTs;
    });
    if (active.length > 0) return active;
    // Fallback: use the most recent effectiveDate per plan grouping (or simply the latest N rows)
    const sorted = [...list].sort((a: any, b: any) => {
      const aEff = a.effectiveDate ? (parseDateLocal(a.effectiveDate)?.getTime() ?? 0) : 0;
      const bEff = b.effectiveDate ? (parseDateLocal(b.effectiveDate)?.getTime() ?? 0) : 0;
      return bEff - aEff;
    });
    // Heuristic: take the latest set for that employee (top unique planIds)
    const seen = new Set<number>();
    const latestSet: any[] = [];
    for (const e of sorted) {
      const pid = Number(e.planId);
      if (!seen.has(pid)) {
        seen.add(pid);
        latestSet.push(e);
      }
    }
    return latestSet;
  };

  // Build potential participants from terminated/inactive employees not already in DB
  const existingEmpIds = useMemo(() => new Set(persistedParticipants.map((p:any)=> Number(p.employeeId))), [persistedParticipants]);
  const potentialRaw = employees
    .filter((e:any)=> e.terminationDate || String(e.status||'').toLowerCase() !== 'active')
    .filter((e:any)=> !existingEmpIds.has(Number(e.id)))
    .map((e:any)=> ({
      id: -Number(e.id), // negative id marks synthetic
      employeeId: Number(e.id),
      clientId: Number(activeClientId),
      qualifyingEvent: 'termination',
      eventDate: e.terminationDate || new Date(),
      electionDeadline: null,
      coverageEndDate: null,
      coverageType: 'medical',
      monthlyPremium: 0,
      elected: false,
      paymentStatus: 'current',
      notices: [],
      payments: [],
    }));
  const allRaw = [...persistedParticipants, ...potentialRaw];

  // Derived simplified view model (merge employee info), include isNew flag
  const cobraParticipants = allRaw.map((p:any) => {
    const emp = employees.find((e:any) => Number(e.id) === Number(p.employeeId)) || {};
    const terminationDate = p.eventDate ? (parseDateLocal(p.eventDate) ?? new Date()) : new Date();
    const lastCoveredDay = p.lastCoveredDay ? (parseDateLocal(p.lastCoveredDay) ?? terminationDate) : terminationDate;
    const coverageStart = addDays(lastCoveredDay, 1); // coverage starts day after last covered day
    const coverageEndDate = p.coverageEndDate ? (parseDateLocal(p.coverageEndDate) ?? addMonths(coverageStart, 18)) : addMonths(coverageStart, 18);
    const electionDeadline = p.electionDeadline ? (parseDateLocal(p.electionDeadline) ?? addDays(terminationDate, 60)) : addDays(terminationDate, 60); // 60-day election window
    const daysRemaining = differenceInDays(coverageEndDate, new Date());
    const notices = p.id > 0
      ? cobraNotices.filter((n: any) => Number(n.participantId) === Number(p.id))
      : Array.isArray(p.notices) ? p.notices : [];
    const noticesSent = {
      initialNotice: notices.find((n:any)=> n.type==='initial') || { sent:false },
      electionNotice: notices.find((n:any)=> n.type==='election') || { sent:false },
      premiumNotice: notices.find((n:any)=> n.type==='premium') || { sent:false },
      terminationNotice: notices.find((n:any)=> n.type==='termination') || { sent:false },
    };
    const dependents = Array.isArray(p.dependents) ? p.dependents : [];
    const matchedEnrollments = findEnrollmentsAsOf(Number(p.employeeId), terminationDate);
    const basePremium = matchedEnrollments.reduce((sum: number, e: any) => {
      const val = e?.totalPremium != null ? parseFloat(String(e.totalPremium)) : 0;
      return sum + (Number.isFinite(val) ? val : 0);
    }, 0);
    const premiumWithSurcharge = Math.round(basePremium * 1.02 * 100) / 100; // +2%
    const coverageDisplay = matchedEnrollments.length > 1 ? 'Multiple' : (p.coverageType || 'medical');
    return {
      ...p,
      firstName: emp.firstName || p.firstName || '—',
      lastName: emp.lastName || p.lastName || '',
      email: emp.email || p.email || '',
      phone: emp.phone || '',
      department: emp.department || 'Former Employee',
      terminationDate,
      electionDeadline,
      coverageStart,
      coverageEndDate,
      daysRemaining: Math.max(0, daysRemaining),
      noticesSent,
  coverageType: coverageDisplay,
  monthlyPremium: premiumWithSurcharge,
  dependents,
  isNew: Number(p.id) < 0,
  _computed: { enrollments: matchedEnrollments, basePremium }
    };
  });

  // Subject determination: 20+ employees (federal COBRA)
  const headcount = employees.length;
  const isSubject = headcount >= 20;

  // Only count real participants (persisted) for these rollups
  const persistedCount = persistedParticipants.length;
  const activeParticipants = cobraParticipants.filter(p => !p.isNew && p.elected && p.daysRemaining > 0);
  const expiringSoon = activeParticipants.filter(p => p.daysRemaining <= 30);
  const pastDue = activeParticipants.filter(p => {
    const v = String(p.paymentStatus || '').toLowerCase();
    return v === 'late' || v === 'grace' || v === 'grace period' || v === 'past_due' || v === 'past-due';
  });

  // Simple compliance signal: any overdue notices or payment issues => issues
  const noticeIssues = cobraParticipants.some((p: any) => {
    const initialCompliant = !!p.noticesSent.initialNotice.sent;
    const electionSent = !!p.noticesSent.electionNotice.sent;
    const electionOnTime = electionSent && p.noticesSent.electionNotice.date &&
      differenceInCalendarDays(parseDateLocal(p.noticesSent.electionNotice.date) ?? new Date(), p.terminationDate) <= 15;
    const electionCompliant = !!(electionSent && electionOnTime);
    return !(initialCompliant && electionCompliant);
  });
  const hasIssues = noticeIssues || pastDue.length > 0;

  // Mutations: add participant, send notice, add payment
  const addParticipant = useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch('/api/cobra/participants', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed to add participant');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/cobra/participants", activeClientId] });
    }
  });
  const addNotice = useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch('/api/cobra/notices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed to add notice');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/cobra/notices", activeClientId] });
    }
  });

  const updateParticipant = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await fetch(`/api/cobra/participants/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed to update participant');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/cobra/participants", activeClientId] });
    }
  });

  const handleSendNotice = (participantId:number, type:string) => {
    setComposeContext({ participantId, type });
    setMessageDraft(`Automated ${type} notice message.`);
    setComposeOpen(true);
  };

  const submitCompose = () => {
    if (composeContext) {
      addNotice.mutate({ participantId: composeContext.participantId, type: composeContext.type, sent: true, sentDate: new Date(), method: 'portal' });
    }
    setComposeOpen(false);
  };

  // Allow adding a participant from a synthetic row
  const createFromSynthetic = (p: any) => {
    const payload = {
      employeeId: p.employeeId,
      clientId: p.clientId,
      qualifyingEvent: p.qualifyingEvent,
      eventDate: p.eventDate,
      electionDeadline: p.electionDeadline ?? addDays(parseDateLocal(p.eventDate) ?? new Date(), 60),
      coverageEndDate: p.coverageEndDate ?? addMonths(addDays(parseDateLocal(p.eventDate) ?? new Date(), 1), 18),
      coverageType: p.coverageType,
      elected: false,
      paymentStatus: 'current'
    };
    addParticipant.mutate(payload);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/compliance?role=employer")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Compliance
          </Button>
          <h1 className="text-3xl font-bold">COBRA Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Reports
          </Button>
          <Button size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* COBRA Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            COBRA Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Federal COBRA</span>
              <Badge variant={isSubject ? 'default' : 'secondary'} className="text-xs">
                {isSubject ? 'Subject (20+ Employees)' : 'Not Subject (<20 Employees)'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Compliance</span>
              <Badge variant={hasIssues ? 'destructive' : 'default'} className="text-xs">
                {hasIssues ? 'Issues Detected' : 'Compliant'}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {persistedCount}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Total Participants
              </div>
              <div className="text-xs text-gray-500 mt-1">
                All qualifying events
              </div>
            </div>
            
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {activeParticipants.length}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Active Coverage
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Currently enrolled
              </div>
            </div>

            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {expiringSoon.length}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Expiring Soon
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Within 30 days
              </div>
            </div>

            <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {pastDue.length}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Payment Issues
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Late or grace period
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="participants" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="participants">Participants</TabsTrigger>
          <TabsTrigger value="notices">Notice Tracking</TabsTrigger>
          <TabsTrigger value="payments">Payment Status</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Participants Tab */}
        <TabsContent value="participants" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>COBRA Participants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  {/* hint removed per request */}
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Participant</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Termination Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Qualifying Event</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Coverage Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Days Remaining</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cobraParticipants.map((participant: any) => {
                        const beforeDeadline = new Date() <= (parseDateLocal(participant.electionDeadline) ?? new Date(0));
                        const statusText = participant.elected
                          ? (participant.daysRemaining > 0 ? 'Active' : 'Terminated')
                          : (beforeDeadline ? 'Pending Election' : 'Declined');
                        const statusVariant =
                          statusText === 'Active' ? 'default'
                          : statusText === 'Pending Election' ? 'secondary'
                          : statusText === 'Declined' ? 'outline'
                          : 'destructive';

                        return (
                          <tr key={participant.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{participant.firstName} {participant.lastName}</p>
                                <p className="text-xs text-muted-foreground">{participant.department || 'Former Employee'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {format(participant.terminationDate, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3 text-sm">{participant.qualifyingEvent}</td>
                            <td className="px-4 py-3 text-sm">{participant.coverageType}</td>
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant} className="text-xs">
                                {statusText}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {participant.elected
                                ? (participant.daysRemaining > 0 ? `${participant.daysRemaining} days` : 'Ended')
                                : `Election by ${format(participant.electionDeadline,'MMM dd')}`}
                            </td>
                            <td className="px-4 py-3">
                              <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                      View Details
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                      <DialogTitle>COBRA Participant Details</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <Label className="text-sm font-medium">Name</Label>
                                          <p className="text-sm">{participant.firstName} {participant.lastName}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Email</Label>
                                          <p className="text-sm">{participant.email}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Termination Date</Label>
                                          <p className="text-sm">{format(participant.terminationDate, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Coverage Start</Label>
                                          <p className="text-sm">{format(participant.coverageStart, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Election Deadline</Label>
                                          <p className="text-sm">{format(participant.electionDeadline, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Coverage End Date</Label>
                                          <p className="text-sm">{format(participant.coverageEndDate, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <div>
                                          <Label className="text-sm font-medium">Monthly Premium</Label>
                                          <p className="text-sm">${participant.monthlyPremium} <span className="text-xs text-muted-foreground">(+2%)</span></p>
                                        </div>
                                      </div>
                                      
                                      {participant.dependents && participant.dependents.length > 0 && (
                                        <div>
                                          <Label className="text-sm font-medium">Covered Dependents</Label>
                                          <ul className="text-sm mt-1">
                                            {participant.dependents.map((dep: string, idx: number) => (
                                              <li key={idx} className="text-muted-foreground">• {dep}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      <div>
                                        <Label className="text-sm font-medium">Notice Status</Label>
                                        <div className="space-y-2 mt-2">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm">Initial Notice</span>
                                            <Badge variant={participant.noticesSent.initialNotice.sent ? "default" : "destructive"} className="text-xs">
                                              {participant.noticesSent.initialNotice.sent ? 'Sent' : 'Pending'}
                                            </Badge>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm">Election Notice</span>
                                            <Badge variant={participant.noticesSent.electionNotice.sent ? "default" : "destructive"} className="text-xs">
                                              {participant.noticesSent.electionNotice.sent ? 'Sent' : 'Pending'}
                                            </Badge>
                                          </div>
                                        </div>
                                      </div>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                  {participant.isNew && (
                                    <Button size="sm" className="ml-2 text-xs px-2 py-1" onClick={() => createFromSynthetic(participant)} disabled={addParticipant.isPending}>
                                      Add Participant
                                    </Button>
                                  )}
                                  {!participant.isNew && !participant.elected && (
                                    <Button size="sm" variant="outline" className="ml-2 text-xs px-2 py-1" onClick={() => updateParticipant.mutate({ id: participant.id, data: { elected: true } })} disabled={updateParticipant.isPending}>
                                      Mark Elected
                                    </Button>
                                  )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notice Tracking Tab */}
        <TabsContent value="notices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Notice Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Participant</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Initial Notice</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Election Notice</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Premium Notice</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Termination Notice</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Compliance</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cobraParticipants.map((participant: any) => {
                        const initialCompliant = participant.noticesSent.initialNotice.sent;
                        const electionSent = participant.noticesSent.electionNotice.sent;
                        const electionOnTime = electionSent && participant.noticesSent.electionNotice.date &&
                          differenceInCalendarDays(parseDateLocal(participant.noticesSent.electionNotice.date) ?? new Date(), participant.terminationDate) <= 15;
                        const electionCompliant = !!(electionSent && electionOnTime);
                        const overallCompliant = initialCompliant && electionCompliant;

                        return (
                          <tr key={participant.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{participant.firstName} {participant.lastName}</p>
                                <p className="text-xs text-muted-foreground">
                                  Terminated: {format(participant.terminationDate, 'MMM dd')}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <Badge variant={initialCompliant ? "default" : "destructive"} className="text-xs">
                                  {initialCompliant ? 'Sent' : 'Overdue'}
                                </Badge>
                                {initialCompliant && (
                                  <p className="text-xs text-muted-foreground">
                                    {format(participant.noticesSent.initialNotice.date, 'MMM dd')} ({participant.noticesSent.initialNotice.method})
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <Badge variant={electionCompliant ? "default" : "destructive"} className="text-xs">
                                  {electionSent ? (electionOnTime ? 'On-Time' : 'Late') : 'Overdue'}
                                </Badge>
                                {electionSent && (
                                  <p className="text-xs text-muted-foreground">
                                    {format(participant.noticesSent.electionNotice.date, 'MMM dd')} ({participant.noticesSent.electionNotice.method})
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <Badge variant={participant.noticesSent.premiumNotice.sent ? "default" : "outline"} className="text-xs">
                                  {participant.noticesSent.premiumNotice.sent ? 'Sent' : 'N/A'}
                                </Badge>
                                {participant.noticesSent.premiumNotice.sent && (
                                  <p className="text-xs text-muted-foreground">
                                    {format(participant.noticesSent.premiumNotice.date, 'MMM dd')} ({participant.noticesSent.premiumNotice.method})
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <Badge variant={participant.noticesSent.terminationNotice.sent ? "default" : "outline"} className="text-xs">
                                  {participant.noticesSent.terminationNotice.sent ? 'Sent' : 'N/A'}
                                </Badge>
                                {participant.noticesSent.terminationNotice.sent && (
                                  <p className="text-xs text-muted-foreground">
                                    {format(participant.noticesSent.terminationNotice.date, 'MMM dd')} ({participant.noticesSent.terminationNotice.method})
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={overallCompliant ? "default" : "destructive"} className="text-xs">
                                {overallCompliant ? 'Compliant' : 'Non-Compliant'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => handleSendNotice(participant.id, 'initial')}>
                                  <Mail className="h-3 w-3 mr-1" />
                                  Send
                                </Button>
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => handleSendNotice(participant.id, 'election')}>
                                  <FileText className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Status Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Payment Status & Duration Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Participant</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Monthly Premium</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Payment Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Coverage Remaining</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">End Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeParticipants.map((participant: any) => {
                        const paymentVariant = 
                          participant.paymentStatus === 'Current' ? 'default' :
                          participant.paymentStatus === 'Late' ? 'destructive' :
                          participant.paymentStatus === 'Grace Period' ? 'secondary' : 'outline';

                        const durationVariant = 
                          participant.daysRemaining > 90 ? 'default' :
                          participant.daysRemaining > 30 ? 'secondary' : 'destructive';

                        return (
                          <tr key={participant.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{participant.firstName} {participant.lastName}</p>
                                <p className="text-xs text-muted-foreground">{participant.coverageType}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">
                              ${participant.monthlyPremium}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={paymentVariant} className="text-xs">
                                {participant.paymentStatus}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <Badge variant={durationVariant} className="text-xs">
                                  {participant.daysRemaining} days
                                </Badge>
                                <div className="text-xs text-muted-foreground">
                                  {Math.floor(participant.daysRemaining / 30)} months
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {format(participant.coverageEndDate, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1" disabled>
                                  Update
                                </Button>
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => handleSendNotice(participant.id, 'premium')}>
                                  <Mail className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab - left intentionally unimplemented */}
        <TabsContent value="reports" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Reports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Notice Compliance Report
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Calendar className="h-4 w-4 mr-2" />
                  Election Tracking Report
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Clock className="h-4 w-4 mr-2" />
                  Coverage Duration Report
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Non-Compliance Alert Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Reports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Premium Collection Report
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Clock className="h-4 w-4 mr-2" />
                  Payment Status Summary
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Delinquency Report
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Download className="h-4 w-4 mr-2" />
                  Monthly COBRA Statement
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compose Notice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {composeContext && <p className="text-xs text-muted-foreground">Notice Type: {composeContext.type}</p>}
            <Label className="text-sm font-medium">Message</Label>
            <textarea className="w-full border rounded p-2 text-sm h-32" value={messageDraft} onChange={e=>setMessageDraft(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={()=> setComposeOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={submitCompose} disabled={addNotice.isPending}>Send</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
